/**
 * useVisitTask — IDB-backed replacement for useMultiTaskVisit (#1471 Phase 3b).
 *
 * Same public interface as useMultiTaskVisit so StoreDetail can swap the
 * import in Phase 4 with zero call-site changes.
 *
 * Storage model (#1897 — 1 row per atomic work session):
 *   - Visit-level fields (storeId, arrivalTime, driveData, gpsData) live
 *     in `visit_singletons` under key='current_visit'.
 *   - Each work session is its own record in `visit_task_sessions` keyed
 *     by a client-generated UUID. ONE record = ONE atomic start/end pair
 *     = ONE CallLog row at submit. A task paused N times yields N+1
 *     records (N closed paused + 1 active or finished).
 *   - `status` reflects how THAT session ended: active/paused/completed/
 *     abandoned. UI lists are derived: currentTask=the one active record,
 *     pausedTasks=latest paused-record-per-task, completedTasks=all
 *     completed records.
 *
 * In-memory state mirrors the old MultiTaskVisitState shape so consumers
 * read synchronously. Mutations update IDB first, then update state.
 *
 * No sync triggers here — pure storage port. Phase 4 adds
 * enqueueSyncAction() at start/finish/checkout.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { callLogsApi, storeTasksApi, ApiError } from '../services/api';
import type { PendingTask, CompletedTask, BulkCallLogRequest } from '../services/api';
import {
  type FileQueueRecord,
  type SurveyResponseEntry,
  type TaskSessionRecord,
  deleteFileQueueEntry,
  deleteSingleton,
  deleteTaskSession,
  generateTaskUUID,
  getFileQueueEntry,
  getFileQueueForTask,
  getSingleton,
  getSurveyResponse,
  getTaskSession,
  getTaskSessionsByStatus,
  putFileQueueEntry,
  putSingleton,
  putSurveyResponse,
  putTaskSession,
} from '../utils/visitDB';
import { emit as emitEvent } from '../utils/eventLog';
import { partitionSessions } from '../utils/timeAllocation';
import { clear as clearStore, STORES } from '../utils/indexedDB';
import { toWireDateTime, toNaiveLocal } from '../utils/visitTime';
import { clearLoneOdometer } from '../utils/driveSegment';

/**
 * Outcome of a checkout bulk-submit (#3430). `ok` clears the visit; a failure
 * carries `terminal` so the UI can STOP looping the rep on "retry" when the
 * server permanently rejected the visit (a 4xx validation) versus a transient
 * error (5xx / network) that a retry can still clear.
 */
export type CheckoutSubmitResult =
  /**
   * `callLogIds` are the rows the server just created, in the order the tasks
   * closed. The submit already resolves them (the response echoes each task's
   * idempotency key) and used to keep them to itself, which left the checkout
   * event with no way to name the visit it was closing (#3537).
   */
  | { ok: true; callLogIds: number[] }
  | { ok: false; terminal: boolean; message: string };

/**
 * Classify a checkout bulk-submit failure. A 4xx is a validation the server
 * will keep rejecting on a blind retry (terminal — the rep must correct the
 * data or leave); a 5xx / network error is transient and worth retrying.
 * ApiError carries the HTTP status; anything else is treated as transient.
 */
export function classifyCheckoutSubmitError(err: unknown): { terminal: boolean; message: string } {
  const message = err instanceof Error ? err.message : 'Failed to submit tasks';
  if (err instanceof ApiError) {
    return { terminal: err.status >= 400 && err.status < 500, message };
  }
  return { terminal: false, message };
}

// Shapes consumed by StoreDetail + its subcomponents. Matching existing
// UploadedPhoto / UploadedDocument / QuestionResponse types keeps the
// consumer rewire mechanical.
export interface QuestionResponse {
  qid: number;
  responseText: string;
}

// Narrow shapes so they assign cleanly to UploadedPhoto / UploadedDocument
// in PhotoUpload / DocumentUpload components without casts.
export interface UploadedPhotoLike {
  id: string;
  name: string;
  type: 'photo';
  dataUrl: string; // blob: URL — cheaper than base64, same use in <img src>
  file: File;
  timestamp: Date;
  /** Opaque reference to the IDB row so remove() can find it. */
  __recordId: number;
}
export interface UploadedDocumentLike {
  id: string;
  name: string;
  type: 'document';
  dataUrl: string;
  file: File;
  timestamp: Date;
  __recordId: number;
}

// Re-exported types — same shape as useMultiTaskVisit so Phase 4 can simply
// change the import path.
export interface DriveToStoreData {
  driveStartTime: Date;
  driveEndTime: Date;
  startOdometer: number | null;
  endOdometer: number | null;
  storeCheckInTime: Date;
}

export interface GPSData {
  lat: number;
  lon: number;
  accuracy?: number;
  overrideReason?: string;
}

export interface PausedTask {
  task: PendingTask;
  /**
   * Accumulated minutes across this task's prior closed sessions in the
   * current visit. Useful for UI summary chips that want to show "task X has
   * 15 min on the clock so far." NOT used at submit time — each session is
   * its own row with its own duration (#1897). May be 0 if the rep just
   * paused after a fresh start with no prior sessions.
   */
  accumulatedMinutes: number;
  /** UUID of the latest paused record — used by resumePausedTask to identify which task. */
  uuid: string;
}

export interface MultiTaskVisitState {
  storeId: number | null;
  arrivalTime: Date | null;
  currentTask: PendingTask | null;
  currentTaskStartTime: Date | null;
  currentTaskCallLogId: number | null;
  /**
   * All tasks currently paused (latest-paused-record per task, status=paused).
   * Was a single slot prior to #1897, which caused the ping-pong corruption
   * bug where pausing a second task wiped the first. Consumers iterate the
   * array to render one resume chip per paused task.
   */
  pausedTasks: PausedTask[];
  completedTasks: CompletedTask[];
  driveData: DriveToStoreData | null;
  gpsData: GPSData | null;
}

interface UseVisitTaskResult {
  visitState: MultiTaskVisitState;
  isLoading: boolean;
  /** True once the visit state has finished hydrating from IDB. Consumers that
   * make start/resume decisions on mount must wait for this so they don't act
   * on an empty pre-hydration snapshot (the task re-spawn bug, #2016/diag #44). */
  isHydrated: boolean;
  error: string | null;

  startVisit: (storeId: number, driveData?: DriveToStoreData, gpsData?: GPSData) => void;
  setDriveData: (driveData: DriveToStoreData) => void;
  setGPSData: (gpsData: GPSData) => void;
  startTask: (
    task: PendingTask,
    callLogId?: number,
    startTimeOverride?: Date,
    options?: { adoptUUID?: string }
  ) => void;
  /**
   * Merge authoritative #2022 workflow flags (requires_order/inventory/oos)
   * onto the active task + its IDB record. The auto-start-on-check-in path
   * builds currentTask from nav params that omit these flags; StoreDetail calls
   * this once the pending-tasks payload arrives so the order gate (and offline
   * rehydrate) see the real per-project requirements. No-op when there is no
   * active task or the flags are already current. (#2022 Phase-2 live-bug fix.)
   */
  reconcileCurrentTaskWorkflow: (config: {
    requires_order: boolean;
    requires_inventory: boolean;
    requires_oos: boolean;
    min_order_amount?: number | null;
    max_product_count?: number | null;
  }) => void;
  pauseCurrentTask: () => PausedTask | null;
  /**
   * Resume a paused task. Pass `taskKeyArg` (e.g. `${task_type}:${task_id}:${project_id}`)
   * to select among multiple paused tasks. Omit it to resume the most-
   * recent paused task (back-compat for single-paused-slot callers).
   */
  resumePausedTask: (taskKeyArg?: string) => void;
  discardPausedTask: (taskKeyArg: string) => void;
  finishCurrentTask: (endTimeOverride?: Date) => CompletedTask | null;
  // Returns the list of CallLog IDs that were voided server-side so the
  // caller can reflect this in day-tracking state (see #1507 — drive-back
  // odo must not be PATCHed onto a voided CallLog at end-of-day).
  abandonCurrentTask: () => number[];
  getCompletedTaskIds: () => number[];
  getPausedTaskId: () => number | null;
  getTotalTimeMinutes: () => number;
  /**
   * The active task's visit localId (the visit_task_sessions uuid). This is
   * the canonical client id the sync queue keys on — dependent writes
   * (survey/photo/doc/checkout) pass it as visitLocalId so the queue resolves
   * the server calllogid lazily, instead of the resolveVisitLocalId(callLogId)
   * bridge which is null until the check-in has synced. Null when no active task.
   */
  getCurrentTaskLocalId: () => string | null;

  // Survey responses for the currently-active task (or most recent paused/
  // unfinished task on rehydrate). Auto-loaded from IDB when currentTask
  // changes. Saving upserts immediately.
  surveyResponses: QuestionResponse[];
  saveSurveyResponse: (qid: number, responseText: string) => Promise<void>;

  // Photos / documents for the currently-active task. Stored as Blobs in
  // visit_file_queue; rendered via blob: URLs. addPhoto/addDocument expect
  // the File straight from an <input type="file">.
  photoUploads: UploadedPhotoLike[];
  documentUploads: UploadedDocumentLike[];
  addPhoto: (file: File) => Promise<void>;
  addDocument: (file: File) => Promise<void>;
  removePhoto: (id: string) => Promise<void>;
  removeDocument: (id: string) => Promise<void>;
  // Re-read a queued file's blob from IDB and mint a fresh object URL. Used to
  // self-heal thumbnails after iOS evicts the blob backing (#2127, diag #54).
  recoverFileBlobUrl: (uploadId: string) => Promise<string | null>;

  submitAllTasks: (
    managerName: string,
    signature?: string,
    noSignatureReason?: string,
    options?: { teamproject?: boolean; teamleader?: boolean; notes?: string; timeAllocation?: { visitStartISO: string; visitEndISO: string; perProject: { projectId: number; minutes: number }[] } | null }
  ) => Promise<CheckoutSubmitResult>;
  resetVisit: () => void;
}

// ---------- Singleton payload ----------

const SINGLETON_KEY = 'current_visit';

// Shape stored in visit_singletons. Dates are serialised as ISO strings to
// match the rest of the visitDB conventions; we revive them on load.
interface StoredVisitMeta {
  storeId: number | null;
  arrivalTime: string | null;
  driveData: {
    driveStartTime: string;
    driveEndTime: string;
    startOdometer: number | null;
    endOdometer: number | null;
    storeCheckInTime: string;
  } | null;
  gpsData: GPSData | null;
  /** UUID of the currently-active task record (status='active'), if any */
  currentTaskUUID: string | null;
  /** Start time (ISO) of current active session */
  currentTaskStartTime: string | null;
  /** callLogId stamped at check-in if the task already had a CallLog created */
  currentTaskCallLogId: number | null;
}

const emptyMeta: StoredVisitMeta = {
  storeId: null,
  arrivalTime: null,
  driveData: null,
  gpsData: null,
  currentTaskUUID: null,
  currentTaskStartTime: null,
  currentTaskCallLogId: null,
};

const initialState: MultiTaskVisitState = {
  storeId: null,
  arrivalTime: null,
  currentTask: null,
  currentTaskStartTime: null,
  currentTaskCallLogId: null,
  pausedTasks: [],
  completedTasks: [],
  driveData: null,
  gpsData: null,
};

/** Stable identity for a task — a paused record matches a resume click by this key. */
function taskKey(t: { task_type: string; task_id: number; project_id: number }): string {
  return `${t.task_type}:${t.task_id}:${t.project_id}`;
}

type TaskIdentity = { task_type: string; task_id: number; project_id: number };

/** Same logical task? project + type must match; a synthetic task_id 0 (project-
 * level nav-back / picker paths) is a wildcard on EITHER side, so it collapses
 * with the real route/set id. Two DISTINCT real ids never match — a project with
 * two different sets at one store stays two tasks. Mirrors the resume-detection
 * rule so clear/dedup/reuse all agree on identity. */
export function sameTaskIdentity(a: TaskIdentity, b: TaskIdentity): boolean {
  return (
    a.project_id === b.project_id &&
    a.task_type === b.task_type &&
    (a.task_id === b.task_id || a.task_id === 0 || b.task_id === 0)
  );
}

/** Identity view of an IDB session record (camelCase → the underscore shape). */
function recIdentity(r: { taskType: string; taskId: number; projectId: number }): TaskIdentity {
  return { task_type: r.taskType, task_id: r.taskId, project_id: r.projectId };
}

/** Build the latest-paused-record-per-task list (for the resume UI). */
function buildPausedList(pausedRecords: TaskSessionRecord[]): PausedTask[] {
  // Sort newest-first, then dedupe by task IDENTITY keeping the newest entry.
  // Uses the 0-aware rule so a synthetic-0 and a real-id record for the same
  // project collapse into ONE chip (strict taskKey used to show both).
  const sorted = [...pausedRecords].sort((a, b) => b.startTime.localeCompare(a.startTime));
  const out: PausedTask[] = [];
  for (const r of sorted) {
    const rid = recIdentity(r);
    if (out.some((k) => sameTaskIdentity(k.task, rid))) continue;
    out.push({
      task: {
        task_type: r.taskType,
        task_id: r.taskId,
        project_id: r.projectId,
        project_description: r.projectDescription,
        client_name: r.clientName ?? null,
        distributor_id: r.distributorId ?? null,
        requires_order: r.requiresOrder,
        requires_inventory: r.requiresInventory,
        requires_oos: r.requiresOos,
        min_order_amount: r.minOrderAmount,
        max_product_count: r.maxProductCount,
      } as PendingTask,
      // Sum durations of every paused session for this task in the visit.
      accumulatedMinutes: pausedRecords
        .filter((p) => p.taskId === r.taskId && p.projectId === r.projectId && p.taskType === r.taskType)
        .reduce((s, p) => s + (p.durationMinutes ?? 0), 0),
      uuid: r.uuid,
    });
  }
  // Return oldest-paused-first so chips render in pause-order
  return out.reverse();
}

function toDriveToStoreData(d: StoredVisitMeta['driveData']): DriveToStoreData | null {
  if (!d) return null;
  return {
    driveStartTime: new Date(d.driveStartTime),
    driveEndTime: new Date(d.driveEndTime),
    startOdometer: d.startOdometer,
    endOdometer: d.endOdometer,
    storeCheckInTime: new Date(d.storeCheckInTime),
  };
}

function fromDriveToStoreData(d: DriveToStoreData): StoredVisitMeta['driveData'] {
  return {
    driveStartTime: d.driveStartTime.toISOString(),
    driveEndTime: d.driveEndTime.toISOString(),
    startOdometer: d.startOdometer,
    endOdometer: d.endOdometer,
    storeCheckInTime: d.storeCheckInTime.toISOString(),
  };
}

function recordToCompletedTask(r: TaskSessionRecord): CompletedTask | null {
  if (!r.endTime) return null;
  return {
    task_type: r.taskType,
    task_id: r.taskId,
    project_id: r.projectId,
    project_description: r.projectDescription,
    client_name: r.clientName,
    start_time: r.startTime,
    end_time: r.endTime,
    duration_minutes: r.durationMinutes ?? 0,
    calllog_id: r.callLogId ?? undefined,
    // #3022 — carry the persisted team config so checkout resolves team status
    // from the completed task itself (survives set-completion + reload).
    is_team_project: r.isTeamProject,
    is_current_user_lead: r.isCurrentUserLead,
  };
}

function pendingTaskFromRecord(r: TaskSessionRecord, fallback: PendingTask | null): PendingTask | null {
  // We persist the full PendingTask for paused tasks separately; completed
  // tasks only need the CompletedTask shape. For the active task we rebuild
  // a minimal PendingTask from the record fields. Optional PendingTask
  // fields we don't track (distributor_id, set-/call-specifics) are left
  // null/undefined — that's fine, StoreDetail only reads the core fields
  // off the currentTask.
  if (fallback) return fallback;
  return {
    task_type: r.taskType,
    task_id: r.taskId,
    project_id: r.projectId,
    project_description: r.projectDescription,
    client_name: r.clientName ?? null,
    distributor_id: r.distributorId ?? null,
    requires_order: r.requiresOrder,
    requires_inventory: r.requiresInventory,
    requires_oos: r.requiresOos,
    min_order_amount: r.minOrderAmount,
    max_product_count: r.maxProductCount,
    // #3022 — carry the persisted team config so a rehydrated active/paused task
    // keeps its deterministic team status after a reload.
    is_team_project: r.isTeamProject,
    is_current_user_lead: r.isCurrentUserLead,
  } as PendingTask;
}

// ---------- Hook ----------

export function useVisitTask(): UseVisitTaskResult {
  const [visitState, setVisitState] = useState<MultiTaskVisitState>(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const metaRef = useRef<StoredVisitMeta>(emptyMeta);
  const activePendingTaskRef = useRef<PendingTask | null>(null);
  // Active task UUID, updated synchronously inside each state-mutating
  // method so downstream work (saveSurveyResponse, addPhoto, addDocument)
  // doesn't have to wait for the async singleton write. This avoids a race
  // where startVisit's IIFE overwrites the singleton before startTask's
  // IIFE sets currentTaskUUID.
  const currentTaskUUIDRef = useRef<string | null>(null);
  // Sum of paused-session durations (in minutes) for the CURRENT active task,
  // captured at startTask time. Used by finishCurrentTask + the running-timer
  // UI to display "task A: 30 min" when the rep finishes A after pausing it
  // once or more. NOT used at submit — each session ships as its own row.
  // Resets to 0 on pause/finish/abandon/visit-reset.
  const currentTaskAccumulatedMinRef = useRef<number>(0);
  // Per-task auxiliary state (surveys + files for the current task)
  const [surveyResponses, setSurveyResponses] = useState<QuestionResponse[]>([]);
  const [photoUploads, setPhotoUploads] = useState<UploadedPhotoLike[]>([]);
  const [documentUploads, setDocumentUploads] = useState<UploadedDocumentLike[]>([]);
  // ONE stable blob: URL per file record, keyed by record id (#2127, diag #55).
  // Reusing a single URL per record across task switches means we NEVER revoke a
  // URL that's still on screen — the old revoke-then-remint-on-every-hydrate
  // churn blanked thumbnails on iOS ("images disappear as I switch tasks").
  // URLs live until the record is removed (removeFileById) or the visit is torn
  // down (no-task / reset / unmount). iOS eviction is healed by recoverFileBlobUrl.
  const blobUrlByRecordRef = useRef<Map<number, string>>(new Map());
  const stableUrlForRecord = (rec: FileQueueRecord): string => {
    const cached = blobUrlByRecordRef.current.get(rec.id);
    if (cached) return cached;
    const url = URL.createObjectURL(rec.blob);
    blobUrlByRecordRef.current.set(rec.id, url);
    return url;
  };
  const revokeAllBlobUrls = (): void => {
    blobUrlByRecordRef.current.forEach((url) => URL.revokeObjectURL(url));
    blobUrlByRecordRef.current.clear();
  };

  // Load from IDB on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const meta = (await getSingleton<StoredVisitMeta>(SINGLETON_KEY)) ?? emptyMeta;
        if (cancelled) return;
        metaRef.current = meta;

        const [activeRecords, pausedRecords, completedRecords] = await Promise.all([
          getTaskSessionsByStatus('active'),
          getTaskSessionsByStatus('paused'),
          getTaskSessionsByStatus('completed'),
        ]);
        if (cancelled) return;

        const activeRec = activeRecords[0]; // at most one
        const currentTask = activeRec
          ? pendingTaskFromRecord(activeRec, activePendingTaskRef.current)
          : null;
        if (currentTask) activePendingTaskRef.current = currentTask;
        currentTaskUUIDRef.current = activeRec?.uuid ?? null;
        // Re-derive accumulated paused minutes for the (rehydrated) active task
        // so the finish chip shows the correct total after an app reload.
        currentTaskAccumulatedMinRef.current = activeRec
          ? pausedRecords
              .filter(
                (p) =>
                  p.taskId === activeRec.taskId &&
                  p.projectId === activeRec.projectId &&
                  p.taskType === activeRec.taskType
              )
              .reduce((s, p) => s + (p.durationMinutes ?? 0), 0)
          : 0;

        // Exclude any paused record that is stale relative to current state:
        //   1. The currently-active task — showing both "current: A" and
        //      "resume: A" would confuse the rep.
        //   2. A task that already has a completed record for the same
        //      (taskId, projectId, taskType) tuple — Rob's diagnostic #24
        //      showed reps trapped in a paused-task loop after finishing,
        //      because a leftover paused IDB row persisted past the
        //      completion of the same task. (#1946)
        const pausedTasks = buildPausedList(
          pausedRecords.filter((p) => {
            const pid = recIdentity(p);
            // 0-aware: a completed (or the active) record clears a same-project
            // paused row even when one side carries the synthetic task_id 0.
            if (completedRecords.some((c) => sameTaskIdentity(recIdentity(c), pid))) return false;
            if (activeRec && sameTaskIdentity(recIdentity(activeRec), pid)) return false;
            return true;
          })
        );

        const completed: CompletedTask[] = completedRecords
          .map(recordToCompletedTask)
          .filter((c): c is CompletedTask => c !== null)
          .sort((a, b) => a.start_time.localeCompare(b.start_time));

        setVisitState({
          storeId: meta.storeId,
          arrivalTime: meta.arrivalTime ? new Date(meta.arrivalTime) : null,
          currentTask,
          currentTaskStartTime: meta.currentTaskStartTime ? new Date(meta.currentTaskStartTime) : null,
          currentTaskCallLogId: meta.currentTaskCallLogId,
          pausedTasks,
          completedTasks: completed,
          driveData: toDriveToStoreData(meta.driveData),
          gpsData: meta.gpsData,
        });
      } catch (err) {
        console.error('[useVisitTask] failed to hydrate:', err);
      } finally {
        if (!cancelled) setIsHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const persistMeta = useCallback(async (next: StoredVisitMeta) => {
    metaRef.current = next;
    await putSingleton(SINGLETON_KEY, next);
  }, []);

  const startVisit = useCallback(
    (storeId: number, driveData?: DriveToStoreData, gpsData?: GPSData) => {
      // Idempotency guard: if we already have an active visit at THIS
      // storeId with in-flight task/survey/file state, treat this call as
      // a no-op navigation re-mount rather than a fresh check-in. Without
      // this, remounting StoreDetail (e.g. after going to /order and back)
      // wipes the current task's survey answers and file queue.
      if (metaRef.current.storeId === storeId && metaRef.current.arrivalTime) {
        // Optionally refresh drive/GPS if newly provided
        if (driveData || gpsData) {
          void (async () => {
            await persistMeta({
              ...metaRef.current,
              driveData: driveData ? fromDriveToStoreData(driveData) : metaRef.current.driveData,
              gpsData: gpsData ?? metaRef.current.gpsData,
            });
          })();
          setVisitState((prev) => ({
            ...prev,
            driveData: driveData ?? prev.driveData,
            gpsData: gpsData ?? prev.gpsData,
          }));
        }
        return;
      }
      // Fresh visit — persist the new singleton but DON'T wipe task /
      // survey / file stores. They're cleared by explicit resetVisit()
      // (checkout flow) or by the startDay cleanup path. Wiping here on
      // every StoreDetail mount (including nav-back remounts, where the
      // idempotency guard can't fire yet because hydration hasn't run)
      // would delete in-flight work.
      void (async () => {
        const meta: StoredVisitMeta = {
          ...emptyMeta,
          storeId,
          arrivalTime: (driveData?.storeCheckInTime ?? new Date()).toISOString(),
          driveData: driveData ? fromDriveToStoreData(driveData) : null,
          gpsData: gpsData ?? null,
        };
        await persistMeta(meta);
      })();
      activePendingTaskRef.current = null;
      currentTaskUUIDRef.current = null;
      setVisitState({
        ...initialState,
        storeId,
        arrivalTime: driveData?.storeCheckInTime ?? new Date(),
        driveData: driveData ?? null,
        gpsData: gpsData ?? null,
      });
      setError(null);
    },
    [persistMeta]
  );

  const setDriveData = useCallback(
    (driveData: DriveToStoreData) => {
      void persistMeta({
        ...metaRef.current,
        driveData: fromDriveToStoreData(driveData),
        arrivalTime: driveData.storeCheckInTime.toISOString(),
      });
      setVisitState((prev) => ({ ...prev, driveData, arrivalTime: driveData.storeCheckInTime }));
    },
    [persistMeta]
  );

  const setGPSData = useCallback(
    (gpsData: GPSData) => {
      void persistMeta({ ...metaRef.current, gpsData });
      setVisitState((prev) => ({ ...prev, gpsData }));
    },
    [persistMeta]
  );

  // `startTimeOverride` is set by the consolidated manual entry flow (#1863)
  // so a synthetic task gets the rep's pre-entered time-in instead of the
  // live clock. Live callers continue to omit it and get new Date() as today.
  const startTask = useCallback(
    (
      task: PendingTask,
      callLogId?: number,
      startTimeOverride?: Date,
      options?: { adoptUUID?: string }
    ) => {
      // ADOPTION PATH (#1987 Phase 3 — queue is sole write path).
      // When the rep checks in, the check-in op has ALREADY written a
      // visit_task_sessions row keyed by the check-in localId (uuid =
      // checkInKey) and enqueued the POST. If we minted a brand-new active
      // uuid here, the server callLogId that checkInOp.reconcile stamps onto
      // the check-in row would be ORPHANED from the active session — breaking
      // checkout/survey, which key on the active uuid. Instead, adopt the
      // check-in row as THE active task so there is ONE id per visit: the
      // queue ops (checkOut/submitSurvey/...) resolve {calllogid} lazily from
      // this row once it syncs. Purely additive — only runs when the nav
      // handoff passes ?visitId= (options.adoptUUID).
      const adoptUUID = options?.adoptUUID;
      if (adoptUUID) {
        // Idempotent: the StoreDetail auto-start effect can fire twice.
        if (currentTaskUUIDRef.current === adoptUUID && activePendingTaskRef.current) {
          return;
        }
        activePendingTaskRef.current = task;
        currentTaskUUIDRef.current = adoptUUID;
        void (async () => {
          const existingRec = await getTaskSession(adoptUUID);
          // Auto-pause any OTHER open task (e.g. rep checked into a second
          // store without finishing the first) so its wall-clock isn't lost.
          const now = startTimeOverride ?? new Date();
          const others = (await getTaskSessionsByStatus('active')).filter(
            (r) => r.uuid !== adoptUUID
          );
          for (const openRec of others) {
            const minutes = Math.max(
              0,
              Math.round((now.getTime() - new Date(openRec.startTime).getTime()) / 60000)
            );
            await putTaskSession({
              ...openRec,
              endTime: now.toISOString(),
              durationMinutes: minutes,
              status: 'paused',
            });
          }
          // Accumulated prior-paused minutes for this task identity so the
          // finish chip stays correct across a nav-away/back resume (#2002).
          const pausedForThis = (await getTaskSessionsByStatus('paused')).filter(
            (p) =>
              p.projectId === task.project_id &&
              p.taskType === task.task_type &&
              (p.taskId === task.task_id || task.task_id === 0 || p.taskId === 0)
          );
          currentTaskAccumulatedMinRef.current = pausedForThis.reduce(
            (s, p) => s + (p.durationMinutes ?? 0),
            0
          );
          const startIso = (existingRec?.startTime as string | undefined) ?? now.toISOString();
          const resolvedCallLogId = existingRec?.callLogId ?? callLogId ?? null;
          const record: TaskSessionRecord = {
            // Preserve gps/odometer/store fields the check-in op wrote.
            ...(existingRec ?? {}),
            uuid: adoptUUID,
            taskType: task.task_type,
            taskId: task.task_id,
            projectId: task.project_id,
            projectDescription: task.project_description,
            clientName: task.client_name ?? undefined,
            distributorId: task.distributor_id ?? undefined,
            requiresOrder: task.requires_order ?? undefined,
            requiresInventory: task.requires_inventory ?? undefined,
            requiresOos: task.requires_oos ?? undefined,
            minOrderAmount: task.min_order_amount ?? undefined,
            maxProductCount: task.max_product_count ?? undefined,
            // #3022 — persist the deterministic team config from the assignment.
            isTeamProject: task.is_team_project ?? existingRec?.isTeamProject,
            isCurrentUserLead: task.is_current_user_lead ?? existingRec?.isCurrentUserLead,
            startTime: startIso,
            status: 'active',
            callLogId: resolvedCallLogId,
          };
          await putTaskSession(record);
          await persistMeta({
            ...metaRef.current,
            currentTaskUUID: adoptUUID,
            currentTaskStartTime: startIso,
            currentTaskCallLogId: resolvedCallLogId,
          });
          setVisitState((prev) => ({
            ...prev,
            currentTask: task,
            currentTaskStartTime: new Date(startIso),
            currentTaskCallLogId: resolvedCallLogId,
            pausedTasks: prev.pausedTasks.filter(
              (p) => !(p.task.task_id === task.task_id && p.task.project_id === task.project_id)
            ),
          }));
        })();
        return;
      }

      // Idempotency guard via sync refs (works after hydration or within
      // the same mount). Handles the common "same task, repeated click" case.
      const existing = activePendingTaskRef.current;
      if (
        existing &&
        currentTaskUUIDRef.current &&
        existing.task_id === task.task_id &&
        existing.project_id === task.project_id
      ) {
        if (callLogId && !metaRef.current.currentTaskCallLogId) {
          void persistMeta({ ...metaRef.current, currentTaskCallLogId: callLogId });
          setVisitState((prev) => ({ ...prev, currentTaskCallLogId: callLogId }));
        }
        return;
      }
      activePendingTaskRef.current = task;
      void (async () => {
        const existingActive = await getTaskSessionsByStatus('active');
        // Reuse path: another tab/remount left an active record for the
        // SAME task — adopt it instead of opening a duplicate session.
        // (Synthetic task_id=0 from nav-back URLs matches by project_id.)
        const match = existingActive.find((r) => sameTaskIdentity(recIdentity(r), task));
        if (match) {
          currentTaskUUIDRef.current = match.uuid;
          // Adopting existing active — re-derive accumulated paused minutes
          // so the eventual finish chip is correct after a nav-away/back.
          const pausedForMatch = await getTaskSessionsByStatus('paused');
          currentTaskAccumulatedMinRef.current = pausedForMatch
            .filter(
              (p) =>
                p.taskId === match.taskId &&
                p.projectId === match.projectId &&
                p.taskType === match.taskType
            )
            .reduce((s, p) => s + (p.durationMinutes ?? 0), 0);
          const callLogToUse = callLogId ?? match.callLogId ?? null;
          if (callLogId && !match.callLogId) {
            await putTaskSession({ ...match, callLogId });
          }
          await persistMeta({
            ...metaRef.current,
            currentTaskUUID: match.uuid,
            currentTaskStartTime: match.startTime,
            currentTaskCallLogId: callLogToUse,
          });
          setVisitState((prev) => ({
            ...prev,
            currentTask: task,
            currentTaskStartTime: new Date(match.startTime),
            currentTaskCallLogId: callLogToUse,
            // Resuming via "start" — drop this task from the paused list if it
            // was there (0-aware, so a synthetic-0 paused row is dropped too).
            pausedTasks: prev.pausedTasks.filter((p) => !sameTaskIdentity(p.task, task)),
          }));
          return;
        }
        // No active match. If a *different* task is currently active,
        // auto-pause it first — closing its record as `paused` so its
        // wall-clock isn't lost (#1897). If THIS task has a paused record
        // for the same identity, that record stays as-is (it represents
        // a closed session that will become its own CallLog row at submit);
        // we just open a fresh active record now.
        const now = startTimeOverride ?? new Date();
        for (const openRec of existingActive) {
          const openStart = new Date(openRec.startTime);
          const minutes = Math.max(0, Math.round((now.getTime() - openStart.getTime()) / 60000));
          await putTaskSession({
            ...openRec,
            endTime: now.toISOString(),
            durationMinutes: minutes,
            status: 'paused',
          });
        }
        // Capture prior paused minutes for THIS task (the resume case) so
        // the eventual finish chip shows the cumulative total, not just
        // the last session. Wire submission is unaffected — each paused
        // record still ships as its own row.
        const pausedBefore = await getTaskSessionsByStatus('paused');
        // Match the paused session(s) being resumed by PROJECT identity, not
        // exact task_id (#2002). The Today paused-banner taps navigate with
        // only ?projectId=, so StoreDetail re-starts the task as a synthetic
        // task with task_id=0 — but the paused session was created with the
        // real route/set task_id. A strict task_id match (0 !== realId) made
        // isResume false, skipping the survey/photo rescue and dropping the
        // rep's work. project_id is the reliable task key (RMR rule / #1944),
        // so match on it; treat task_id leniently when either side is the
        // synthetic 0.
        const priorPausedForThis = pausedBefore.filter(
          (p) =>
            p.projectId === task.project_id &&
            p.taskType === task.task_type &&
            (p.taskId === task.task_id || task.task_id === 0 || p.taskId === 0)
        );
        currentTaskAccumulatedMinRef.current = priorPausedForThis.reduce(
          (s, p) => s + (p.durationMinutes ?? 0),
          0,
        );
        // If a prior paused record exists for this exact task identity,
        // the rep is RESUMING — emit task_resumed. Otherwise it's a fresh
        // start. (#1950)
        const isResume = priorPausedForThis.length > 0;
        const uuid = generateTaskUUID();
        currentTaskUUIDRef.current = uuid;
        const record: TaskSessionRecord = {
          uuid,
          taskType: task.task_type,
          taskId: task.task_id,
          projectId: task.project_id,
          projectDescription: task.project_description,
          clientName: task.client_name ?? undefined,
          distributorId: task.distributor_id ?? undefined,
          requiresOrder: task.requires_order ?? undefined,
          requiresInventory: task.requires_inventory ?? undefined,
          requiresOos: task.requires_oos ?? undefined,
          minOrderAmount: task.min_order_amount ?? undefined,
          maxProductCount: task.max_product_count ?? undefined,
          // #3022 — persist the deterministic team config from the assignment.
          isTeamProject: task.is_team_project ?? undefined,
          isCurrentUserLead: task.is_current_user_lead ?? undefined,
          startTime: now.toISOString(),
          status: 'active',
          callLogId: callLogId ?? null,
        };
        await putTaskSession(record);
        await persistMeta({
          ...metaRef.current,
          currentTaskUUID: uuid,
          currentTaskStartTime: now.toISOString(),
          currentTaskCallLogId: callLogId ?? null,
        });

        // CRITICAL data-rescue on resume (#1956): each pause/resume cycle
        // mints a new active task UUID by design (one IDB row per atomic
        // session, per #1897). But survey responses + photos + documents
        // are keyed by task UUID, so without migration they become
        // ORPHANED under the paused UUID and INVISIBLE to the new active
        // session. The rep sees a blank survey on resume and re-enters
        // everything — the field-tested failure mode flagged by diag #29.
        //
        // Fix: copy the most-recent paused record's aux data (survey
        // response record + file queue rows) onto the new active UUID.
        // The paused record stays as-is (its CallLog still ships per the
        // 1-row-per-session model) so this is purely additive.
        if (isResume) {
          // Pick the latest paused record by endTime so chained pauses
          // (pause -> resume -> pause -> resume) carry the most recent
          // input forward.
          const mostRecentPaused = priorPausedForThis
            .slice()
            .sort((a, b) => (b.endTime ?? '').localeCompare(a.endTime ?? ''))[0];
          if (mostRecentPaused) {
            try {
              const [pausedSurvey, pausedFiles] = await Promise.all([
                getSurveyResponse(mostRecentPaused.uuid),
                getFileQueueForTask(mostRecentPaused.uuid),
              ]);
              if (pausedSurvey && pausedSurvey.responses?.length) {
                await putSurveyResponse({
                  taskUUID: uuid,
                  responses: pausedSurvey.responses,
                  updatedAt: new Date().toISOString(),
                });
              }
              for (const fileRec of pausedFiles) {
                // Re-put with the new taskUUID; id stays so we update
                // in place rather than creating a duplicate file row.
                await putFileQueueEntry({ ...fileRec, taskUUID: uuid });
              }
            } catch (err) {
              console.error('[useVisitTask] resume aux-data migration failed', err);
              // Best-effort: the user will need to re-enter, but the
              // primary task flow continues. NOT a blocker.
            }
          }
        }

        // Refresh paused list from IDB so the auto-paused prior task shows up
        // and this task is removed (if it was previously paused — "start" =
        // start fresh, resume is via resumePausedTask).
        const pausedRecsFresh = await getTaskSessionsByStatus('paused');
        const pausedTasksFresh = buildPausedList(
          // Drop any paused records that match THIS task — we're starting
          // fresh, not resuming, so they shouldn't appear in the list. They
          // remain in IDB as closed sessions and still go to the wire.
          pausedRecsFresh.filter(
            (p) => !(p.taskId === task.task_id && p.projectId === task.project_id)
          )
        );
        setVisitState((prev) => ({
          ...prev,
          currentTask: task,
          currentTaskStartTime: now,
          currentTaskCallLogId: callLogId ?? null,
          pausedTasks: pausedTasksFresh,
        }));

        // Emit AFTER state + IDB are consistent. Fresh start vs resume is
        // detected above via prior-paused records. (#1950)
        void emitEvent({
          event_type: isResume ? 'task_resumed' : 'task_started',
          event_at: now,
          project_id: task.project_id,
          calllog_id: callLogId ?? null,
          payload: {
            task_type: task.task_type,
            task_id: task.task_id,
            project_description: task.project_description,
            client_name: task.client_name ?? null,
            distributor_id: task.distributor_id ?? null,
            session_uuid: uuid,
            prior_paused_minutes: currentTaskAccumulatedMinRef.current,
          },
        });
      })();
    },
    [persistMeta]
  );

  const reconcileCurrentTaskWorkflow = useCallback(
    (config: {
      requires_order: boolean;
      requires_inventory: boolean;
      requires_oos: boolean;
      min_order_amount?: number | null;
      max_product_count?: number | null;
    }) => {
      const current = activePendingTaskRef.current;
      if (!current) return;
      const min = config.min_order_amount ?? null;
      const max = config.max_product_count ?? null;
      // Already current? Skip — avoids redundant IDB writes and a render loop
      // (the StoreDetail effect that calls this keys on currentTask identity).
      if (
        current.requires_order === config.requires_order &&
        current.requires_inventory === config.requires_inventory &&
        current.requires_oos === config.requires_oos &&
        (current.min_order_amount ?? null) === min &&
        (current.max_product_count ?? null) === max
      ) {
        return;
      }
      const patch = {
        requires_order: config.requires_order,
        requires_inventory: config.requires_inventory,
        requires_oos: config.requires_oos,
        min_order_amount: min,
        max_product_count: max,
      };
      const updated: PendingTask = { ...current, ...patch };
      activePendingTaskRef.current = updated;
      // Persist onto the active IDB record so the config survives a rehydrate and
      // rides along when this session is later paused/finished (#1897 one-row
      // model copies from currentTask).
      const uuid = currentTaskUUIDRef.current;
      if (uuid) {
        void (async () => {
          const rec = await getTaskSession(uuid);
          if (rec) {
            await putTaskSession({
              ...rec,
              requiresOrder: config.requires_order,
              requiresInventory: config.requires_inventory,
              requiresOos: config.requires_oos,
              minOrderAmount: min,
              maxProductCount: max,
            });
          }
        })();
      }
      setVisitState((prev) =>
        prev.currentTask ? { ...prev, currentTask: { ...prev.currentTask, ...patch } } : prev
      );
    },
    []
  );

  const pauseCurrentTask = useCallback((): PausedTask | null => {
    const state = visitState;
    if (!state.currentTask || !state.currentTaskStartTime) return null;
    const now = new Date();
    const sessionMinutes = Math.max(
      0,
      Math.round((now.getTime() - state.currentTaskStartTime.getTime()) / 60000)
    );

    // Prior paused-session minutes for THIS task (captured at startTask
    // time — see currentTaskAccumulatedMinRef). Used so the resulting
    // paused chip shows "task X: 30 min" after a pause→resume→pause cycle,
    // not just the last session's 15 min. Wire still ships one row per
    // session.
    const priorPausedMin = currentTaskAccumulatedMinRef.current;

    const meta = metaRef.current;
    const closedUUID = meta.currentTaskUUID;
    const paused: PausedTask = {
      task: state.currentTask,
      accumulatedMinutes: priorPausedMin + sessionMinutes,
      uuid: closedUUID ?? '',
    };

    void (async () => {
      if (!closedUUID) return;
      // Close the current active record as a paused session — finalised,
      // never reopened. A resume click later opens a fresh record.
      const existing = await getTaskSession(closedUUID);
      const record: TaskSessionRecord = {
        uuid: closedUUID,
        taskType: state.currentTask!.task_type,
        taskId: state.currentTask!.task_id,
        projectId: state.currentTask!.project_id,
        projectDescription: state.currentTask!.project_description,
        clientName: state.currentTask!.client_name ?? undefined,
        distributorId: state.currentTask!.distributor_id ?? undefined,
        // #3022 — carry the persisted team config onto the paused record.
        isTeamProject: existing?.isTeamProject ?? state.currentTask!.is_team_project,
        isCurrentUserLead: existing?.isCurrentUserLead ?? state.currentTask!.is_current_user_lead,
        startTime: state.currentTaskStartTime!.toISOString(),
        endTime: now.toISOString(),
        durationMinutes: sessionMinutes,
        status: 'paused',
        callLogId: existing?.callLogId ?? state.currentTaskCallLogId,
      };
      await putTaskSession(record);
      await persistMeta({
        ...meta,
        currentTaskUUID: null,
        currentTaskStartTime: null,
        currentTaskCallLogId: null,
      });
    })();

    currentTaskUUIDRef.current = null;
    activePendingTaskRef.current = null;
    // Pause closes the current session. The accumulated counter stays
    // logically tied to the task — but since pause clears currentTask,
    // we reset the ref. resumePausedTask → startTask will re-derive it
    // from the IDB paused records.
    currentTaskAccumulatedMinRef.current = 0;
    setVisitState((prev) => {
      // Replace any existing paused entry for this task with the fresh one
      // (latest paused record per task wins).
      const filtered = prev.pausedTasks.filter(
        (p) =>
          !(
            p.task.task_id === paused.task.task_id &&
            p.task.project_id === paused.task.project_id &&
            p.task.task_type === paused.task.task_type
          )
      );
      const nextPaused = [...filtered, paused];
      return {
        ...prev,
        currentTask: null,
        currentTaskStartTime: null,
        currentTaskCallLogId: null,
        pausedTasks: nextPaused,
      };
    });
    void emitEvent({
      event_type: 'task_paused',
      event_at: now,
      project_id: paused.task.project_id,
      calllog_id: state.currentTaskCallLogId ?? null,
      payload: {
        task_type: paused.task.task_type,
        task_id: paused.task.task_id,
        session_uuid: closedUUID,
        session_minutes: sessionMinutes,
        accumulated_minutes: paused.accumulatedMinutes,
      },
    });
    return paused;
  }, [visitState, persistMeta]);

  /**
   * Resume a paused task. `taskKeyArg` selects which paused task to resume
   * when multiple are paused; omitting it resumes the most-recent paused
   * task (back-compat with the old single-paused-slot signature).
   */
  const resumePausedTask = useCallback(
    (taskKeyArg?: string) => {
      const target = taskKeyArg
        ? visitState.pausedTasks.find((p) => taskKey(p.task) === taskKeyArg)
        : visitState.pausedTasks[visitState.pausedTasks.length - 1];
      if (!target) return;
      // Resuming is just "start a new active session for that task." The
      // existing paused record stays as a closed past session and becomes
      // its own CallLog row at submit (#1897). startTask already handles
      // auto-pausing any other current task.
      startTask(target.task);
    },
    [visitState.pausedTasks, startTask]
  );

  /**
   * Discard a single paused session the rep opened by accident. Removes that
   * task's paused IDB record(s) so they never ship as a CallLog at checkout, and
   * voids any server placeholder they carried. Scoped — unlike
   * abandonCurrentTask, it touches ONLY the selected task's paused rows, leaving
   * the active task and every other paused/completed task intact.
   */
  const discardPausedTask = useCallback(
    (taskKeyArg: string): void => {
      const target = visitState.pausedTasks.find((p) => taskKey(p.task) === taskKeyArg);
      if (!target) return;
      void (async () => {
        const pausedRecords = await getTaskSessionsByStatus('paused');
        const matching = pausedRecords.filter((r) => sameTaskIdentity(recIdentity(r), target.task));
        const voidTargets = matching
          .map((r) => r.callLogId)
          .filter((id): id is number => typeof id === 'number');
        for (const r of matching) await deleteTaskSession(r.uuid);
        for (const callLogId of voidTargets) {
          callLogsApi
            .voidCallLog(callLogId, 'discarded')
            .catch((err) => console.warn(`[useVisitTask] void ${callLogId} failed`, err));
        }
        void emitEvent({
          event_type: 'task_abandoned',
          event_at: new Date(),
          project_id: target.task.project_id ?? null,
          calllog_id: voidTargets[0] ?? null,
          payload: {
            task_type: target.task.task_type,
            task_id: target.task.task_id,
            discarded: true,
            voided_calllog_ids: voidTargets,
          },
        });
      })();
      setVisitState((prev) => ({
        ...prev,
        pausedTasks: prev.pausedTasks.filter((p) => !sameTaskIdentity(p.task, target.task)),
      }));
    },
    [visitState.pausedTasks]
  );

  // `endTimeOverride` is set by the consolidated manual entry flow (#1863)
  // so the rep's pre-entered time-out is recorded as the task end instead
  // of the live clock. Live callers omit it and continue to use new Date().
  /**
   * Finish the current active session. Closes the active IDB record as
   * `completed` — this becomes ONE CallLog row at submit (#1897). Any
   * earlier paused sessions of the SAME task stay in IDB as their own
   * closed paused records and will produce their own CallLog rows.
   *
   * The returned CompletedTask's `duration_minutes` aggregates THIS session
   * + every prior paused session of the same task in this visit, so the UI
   * chip shows "task A: 30 min" (not "15 min, ignoring the earlier 15"). The
   * IDB record stores only this session's duration — wire totals come from
   * summing rows downstream, identical to how the legacy timesheet renders.
   *
   * We also drop matching entries from `pausedTasks` so the UI stops
   * prompting "resume X" after the rep declared X done.
   */
  const finishCurrentTask = useCallback((endTimeOverride?: Date): CompletedTask | null => {
    const state = visitState;
    if (!state.currentTask || !state.currentTaskStartTime) return null;
    // #2465: never let an invalid override produce a bad end_time. endTime.toISOString()
    // (below) throws on an Invalid Date, and a literal "Invalid Date" string poisons the
    // checkout summary. Fall back to now when the override didn't parse.
    const endTime =
      endTimeOverride && !isNaN(endTimeOverride.getTime()) ? endTimeOverride : new Date();
    const sessionMs = endTime.getTime() - state.currentTaskStartTime.getTime();
    const sessionMinutes = Math.max(0, Math.round(sessionMs / 60000));

    // Prior paused minutes for THIS task (captured at startTask time —
    // see currentTaskAccumulatedMinRef). Visible only to the display chip:
    // each paused record will still ship as its own CallLog row at submit.
    const priorPausedMin = currentTaskAccumulatedMinRef.current;

    const completed: CompletedTask = {
      task_type: state.currentTask.task_type,
      task_id: state.currentTask.task_id,
      project_id: state.currentTask.project_id,
      project_description: state.currentTask.project_description,
      client_name: state.currentTask.client_name,
      start_time: state.currentTaskStartTime.toISOString(),
      end_time: endTime.toISOString(),
      duration_minutes: sessionMinutes + priorPausedMin,
      calllog_id: state.currentTaskCallLogId ?? undefined,
      // #3022 — deterministic team config carried onto the in-session completed
      // task so checkout resolves team status immediately (no reload needed).
      is_team_project: state.currentTask.is_team_project,
      is_current_user_lead: state.currentTask.is_current_user_lead,
    };

    const meta = metaRef.current;
    const finishedUUID = meta.currentTaskUUID;
    void (async () => {
      if (!finishedUUID) return;
      const existing = await getTaskSession(finishedUUID);
      // IDB record's durationMinutes = THIS session only. The wire row for
      // this record covers start_time→end_time exactly. The aggregated total
      // is a display-only concern (visible via state.completedTasks).
      const record: TaskSessionRecord = {
        uuid: finishedUUID,
        taskType: completed.task_type,
        taskId: completed.task_id,
        projectId: completed.project_id,
        projectDescription: completed.project_description,
        clientName: completed.client_name ?? undefined,
        distributorId: state.currentTask!.distributor_id ?? existing?.distributorId,
        // #3022 — persist the team config onto the completed record so checkout
        // resolves team status from the completed task after a reload.
        isTeamProject: existing?.isTeamProject ?? state.currentTask!.is_team_project,
        isCurrentUserLead: existing?.isCurrentUserLead ?? state.currentTask!.is_current_user_lead,
        startTime: completed.start_time,
        endTime: completed.end_time,
        durationMinutes: sessionMinutes,
        status: 'completed',
        callLogId: completed.calllog_id ?? existing?.callLogId ?? null,
      };
      await putTaskSession(record);
      await persistMeta({
        ...meta,
        currentTaskUUID: null,
        currentTaskStartTime: null,
        currentTaskCallLogId: null,
      });
    })();
    activePendingTaskRef.current = null;
    currentTaskUUIDRef.current = null;
    currentTaskAccumulatedMinRef.current = 0;

    setVisitState((prev) => {
      // Drop any paused-list entries for this task — rep declared it done.
      // 0-aware identity so a synthetic-0 paused row is cleared by a real-id
      // completion (and vice-versa); otherwise it lingered as a stuck chip.
      const remainingPaused = prev.pausedTasks.filter((p) => !sameTaskIdentity(p.task, completed));
      return {
        ...prev,
        currentTask: null,
        currentTaskStartTime: null,
        currentTaskCallLogId: null,
        pausedTasks: remainingPaused,
        completedTasks: [...prev.completedTasks, completed],
      };
    });
    void emitEvent({
      event_type: 'task_finished',
      event_at: endTime,
      project_id: completed.project_id,
      calllog_id: completed.calllog_id ?? null,
      payload: {
        task_type: completed.task_type,
        task_id: completed.task_id,
        session_uuid: finishedUUID,
        session_minutes: sessionMinutes,
        prior_paused_minutes: priorPausedMin,
        total_duration_minutes: completed.duration_minutes,
      },
    });
    return completed;
  }, [visitState, persistMeta]);

  /**
   * Abandon: discard the active session AND every paused session in this
   * visit, voiding any server-side CallLog rows that were created at
   * check-in time. (#1471 #6) — without this an abandoned CallLog has
   * stcalltime set but edcalltime NULL and pollutes weekly timesheets.
   */
  const abandonCurrentTask = useCallback((): number[] => {
    const meta = metaRef.current;
    const syncVoidTargets: number[] = [];
    if (meta.currentTaskCallLogId) syncVoidTargets.push(meta.currentTaskCallLogId);
    const abandonedTask = activePendingTaskRef.current;
    const abandonedUUID = meta.currentTaskUUID;

    void (async () => {
      const voidTargets: number[] = [...syncVoidTargets];
      const pausedRecords = await getTaskSessionsByStatus('paused');
      for (const p of pausedRecords) {
        if (p.callLogId) voidTargets.push(p.callLogId);
      }
      // Delete active + every paused record — abandon means "this visit's
      // work is gone." Completed records stay (those are already-finished
      // sessions the rep declared done; the rep is abandoning only what's
      // open + paused, not previously completed work).
      if (meta.currentTaskUUID) await deleteTaskSession(meta.currentTaskUUID);
      for (const p of pausedRecords) await deleteTaskSession(p.uuid);
      await persistMeta({
        ...meta,
        currentTaskUUID: null,
        currentTaskStartTime: null,
        currentTaskCallLogId: null,
      });

      for (const callLogId of voidTargets) {
        callLogsApi
          .voidCallLog(callLogId, 'abandoned')
          .catch((err) => console.warn(`[useVisitTask] void ${callLogId} failed`, err));
      }
    })();
    activePendingTaskRef.current = null;
    currentTaskUUIDRef.current = null;
    currentTaskAccumulatedMinRef.current = 0;
    setVisitState((prev) => ({
      ...prev,
      currentTask: null,
      currentTaskStartTime: null,
      currentTaskCallLogId: null,
      pausedTasks: [],
    }));
    void emitEvent({
      event_type: 'task_abandoned',
      event_at: new Date(),
      project_id: abandonedTask?.project_id ?? null,
      calllog_id: meta.currentTaskCallLogId ?? null,
      payload: {
        task_type: abandonedTask?.task_type ?? null,
        task_id: abandonedTask?.task_id ?? null,
        session_uuid: abandonedUUID,
        voided_calllog_ids: syncVoidTargets,
      },
    });
    return syncVoidTargets;
  }, [persistMeta]);

  const getCompletedTaskIds = useCallback(
    (): number[] => visitState.completedTasks.map((t) => t.task_id),
    [visitState.completedTasks]
  );

  const getPausedTaskId = useCallback(
    (): number | null =>
      visitState.pausedTasks[visitState.pausedTasks.length - 1]?.task.task_id ?? null,
    [visitState.pausedTasks]
  );

  // The active task's visit localId (its visit_task_sessions uuid). Read from
  // the ref so callers always get the freshest value within the same tick.
  const getCurrentTaskLocalId = useCallback((): string | null => currentTaskUUIDRef.current, []);

  // Total time = completed sessions + paused sessions. With the 1-row-per-
  // session model (#1897), every closed session contributes its own
  // duration_minutes; paused sessions are tracked in pausedTasks (each
  // entry's accumulatedMinutes = sum of its closed records).
  const getTotalTimeMinutes = useCallback(
    (): number => {
      const completedMin = visitState.completedTasks.reduce((s, t) => s + t.duration_minutes, 0);
      const pausedMin = visitState.pausedTasks.reduce((s, p) => s + p.accumulatedMinutes, 0);
      return completedMin + pausedMin;
    },
    [visitState.completedTasks, visitState.pausedTasks]
  );

  const submitAllTasks = useCallback(
    async (
      managerName: string,
      signature?: string,
      noSignatureReason?: string,
      options?: { teamproject?: boolean; teamleader?: boolean; notes?: string; timeAllocation?: { visitStartISO: string; visitEndISO: string; perProject: { projectId: number; minutes: number }[] } | null }
    ): Promise<CheckoutSubmitResult> => {
      if (!visitState.storeId) {
        setError('No store visit in progress');
        return { ok: false, terminal: true, message: 'No store visit in progress' };
      }
      setIsLoading(true);
      setError(null);
      try {
        // Build one wire entry per closed IDB record (#1897). Both completed
        // AND paused records produce CallLog rows — a paused session is a
        // real chunk of work the rep did, just one they didn't formally
        // "finish." Sorted by start_time so the drive-in attaches to the
        // earliest session and drive-out to the latest (matches legacy
        // bookend convention).
        const [pausedRecs, completedRecs] = await Promise.all([
          getTaskSessionsByStatus('paused'),
          getTaskSessionsByStatus('completed'),
        ]);
        const closedSessions = [...pausedRecs, ...completedRecs]
          .filter((r) => !!r.endTime)
          .sort((a, b) => a.startTime.localeCompare(b.startTime));
        if (closedSessions.length === 0) {
          setError('No tasks to submit');
          return { ok: false, terminal: true, message: 'No tasks to submit' };
        }
        // #2104 Split Time (feature-gated): when the rep allocated time at
        // checkout, replace the recorded session times with a sequential
        // partition of the visit span (no overlap by construction; a project's
        // minutes sub-split across its sessions proportional to actual
        // durations). A null partition (defensive: unparseable span / unmatched
        // project) falls back to the actual recorded times. Only ever set when
        // the split_time_at_checkout gate is on (CheckoutSummary won't produce
        // an allocation otherwise).
        const alloc = options?.timeAllocation;
        const partition = alloc
          ? partitionSessions(
              alloc.visitStartISO,
              alloc.visitEndISO,
              closedSessions.map((rec) => ({
                key: rec.uuid,
                projectId: rec.projectId,
                actualSeconds: Math.max(0, (Date.parse(rec.endTime!) - Date.parse(rec.startTime)) / 1000),
              })),
              alloc.perProject
            )
          : null;
        const partitionByKey = partition ? new Map(partition.map((p) => [p.key, p])) : null;
        // A paused session is real work the rep did but didn't finish. It ships
        // with its hours (rep is paid) but flagged completed=false so the server
        // leaves the set open and excludes it from client billing until finished.
        const pausedUuids = new Set(pausedRecs.map((r) => r.uuid));
        const request: BulkCallLogRequest = {
          store_id: visitState.storeId,
          tasks: closedSessions.map((rec) => ({
            task_type: rec.taskType,
            task_id: rec.taskId,
            project_id: rec.projectId,
            completed: !pausedUuids.has(rec.uuid),
            // #2679: manual-entry sessions can store a bare clock-time end ("10:18")
            // which the server's fromisoformat() rejects, 400-ing the whole visit.
            // Expand bare times to full ISO anchored to the session's own (ISO) start.
            // #2104: a split partition (when present) overrides the recorded times.
            stcalltime: toNaiveLocal(
              partitionByKey?.get(rec.uuid)?.stcalltime ?? toWireDateTime(rec.startTime, rec.startTime) ?? rec.startTime,
            ),
            edcalltime: toNaiveLocal(
              partitionByKey?.get(rec.uuid)?.edcalltime ?? toWireDateTime(rec.endTime!, rec.startTime) ?? rec.endTime!,
            ),
            calllog_id: rec.callLogId ?? undefined,
            // #3020: stable per-task key = the session uuid. The server uses it
            // to UPDATE the row it already created on a retry (no duplicate),
            // and echoes it back so we can persist the calllogid below.
            idempotency_key: rec.uuid,
          })),
          manager_name: managerName || undefined,
          signature: signature || undefined,
          no_signature_reason: noSignatureReason || undefined,
        };
        if (visitState.driveData) {
          request.stdrivetime = toNaiveLocal(visitState.driveData.driveStartTime.toISOString());
          request.eddrivetime = toNaiveLocal(visitState.driveData.driveEndTime.toISOString());
          // A lone reading 400s the whole bulk visit and traps the rep in the
          // checkout retry loop (prod diag #2) — the serializer requires the
          // pair together; an equal pair is a valid zero-mile drive (#3618).
          // Mirrors the check-in / manual-entry paths via the shared guard.
          const { startOdometer, endOdometer } = clearLoneOdometer(
            visitState.driveData.startOdometer ?? undefined,
            visitState.driveData.endOdometer ?? undefined,
          );
          if (startOdometer !== undefined) request.driveodostart = startOdometer;
          if (endOdometer !== undefined) request.driveodoend = endOdometer;
        }
        if (visitState.gpsData) {
          // Round to the server DecimalField precision (lat/lon 6dp, accuracy 2dp).
          // Raw floats have too many total digits and DRF rejects the POST before
          // rounding ("no more than 10 digits in total"). #2958 / diag #69.
          request.lat = Math.round(visitState.gpsData.lat * 1_000_000) / 1_000_000;
          request.lon = Math.round(visitState.gpsData.lon * 1_000_000) / 1_000_000;
          if (visitState.gpsData.accuracy != null)
            request.gps_accuracy = Math.round(visitState.gpsData.accuracy * 100) / 100;
          if (visitState.gpsData.overrideReason) request.gps_override_reason = visitState.gpsData.overrideReason;
        }
        if (options?.teamproject !== undefined) request.teamproject = options.teamproject;
        if (options?.teamleader !== undefined) request.teamleader = options.teamleader;
        if (options?.notes) request.notes = options.notes;
        // #2104 audit marker: allocated time is distinguishable from measured
        // time when a client questions a bill.
        if (partition) request.notes = `${request.notes ? `${request.notes} ` : ''}[time split across tasks]`;

        const response = await storeTasksApi.bulkCreateCallLogs(request);
        if (response.status === 'success') {
          // #3020: persist the server calllogids back onto their IDB sessions
          // (mapped by the echoed idempotency_key = session uuid) so a manual
          // retry UPDATEs rather than duplicates, and any dependent sync op keyed
          // on the session can resolve its parent calllogid. Non-fatal.
          let callLogIds: number[] = [];
          try {
            const byKey = new Map((response.data ?? []).map((d) => [d.idempotency_key, d.calllogid]));
            callLogIds = closedSessions
              .map((rec) => byKey.get(rec.uuid) ?? rec.callLogId)
              .filter((cid): cid is number => typeof cid === 'number');
            await Promise.all(
              closedSessions.map(async (rec) => {
                const cid = byKey.get(rec.uuid);
                if (cid && rec.callLogId !== cid) {
                  await putTaskSession({ ...rec, callLogId: cid });
                }
              })
            );
          } catch (writeBackErr) {
            console.warn('#3020 calllogid write-back failed (non-fatal):', writeBackErr);
          }
          return { ok: true, callLogIds };
        }
        // 200 with an error body: the server handled the request but rejected
        // it on a validation — terminal, a blind retry won't clear it.
        const bodyErr = response.errors?.join(', ') || 'Failed to create call logs';
        setError(bodyErr);
        return { ok: false, terminal: true, message: bodyErr };
      } catch (err) {
        const { terminal, message } = classifyCheckoutSubmitError(err);
        setError(message);
        return { ok: false, terminal, message };
      } finally {
        setIsLoading(false);
      }
    },
    [visitState.storeId, visitState.driveData, visitState.gpsData]
  );

  const resetVisit = useCallback(() => {
    void (async () => {
      await clearStore(STORES.VISIT_TASK_SESSIONS);
      await clearStore(STORES.VISIT_FILE_QUEUE);
      await clearStore(STORES.VISIT_SURVEY_RESPONSES);
      await deleteSingleton(SINGLETON_KEY);
    })();
    activePendingTaskRef.current = null;
    currentTaskUUIDRef.current = null;
    currentTaskAccumulatedMinRef.current = 0;
    metaRef.current = emptyMeta;
    // Full reset — free every cached blob URL.
    revokeAllBlobUrls();
    setVisitState(initialState);
    setSurveyResponses([]);
    setPhotoUploads([]);
    setDocumentUploads([]);
    setError(null);
  }, []);

  // ---------- Survey + file auxiliary state ----------

  /**
   * Load survey + file records for the current task. Runs whenever the
   * current task UUID changes (startTask, resume, hydrate).
   */
  useEffect(() => {
    const taskUUID = currentTaskUUIDRef.current;
    if (!taskUUID) {
      // No active task — nothing on screen, so free the cached URLs.
      revokeAllBlobUrls();
      setSurveyResponses([]);
      setPhotoUploads([]);
      setDocumentUploads([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [surveyRec, files] = await Promise.all([
          getSurveyResponse(taskUUID),
          getFileQueueForTask(taskUUID),
        ]);
        if (cancelled) return;
        setSurveyResponses(surveyRec?.responses ?? []);
        // Rebuild from fresh records, REUSING each record's stable URL (no
        // revoke — the displayed thumbnails keep their live URLs across the
        // switch, so they never blank). #2127 / diag #55.
        const photos: UploadedPhotoLike[] = [];
        const docs: UploadedDocumentLike[] = [];
        for (const rec of files) {
          if (rec.type === 'photo') {
            photos.push(hydratePhotoRecord(rec, stableUrlForRecord(rec)));
          } else {
            docs.push(hydrateDocumentRecord(rec, stableUrlForRecord(rec)));
          }
        }
        setPhotoUploads(photos);
        setDocumentUploads(docs);
      } catch (err) {
        console.error('[useVisitTask] failed to load aux state:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-run when the active task identity changes. visitState.currentTask is
    // the only reliable React-visible trigger; currentTaskStartTime also
    // changes on resume so it makes a good secondary key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitState.currentTask?.task_id, visitState.currentTaskStartTime, isHydrated]);

  // Revoke blob URLs on unmount
  useEffect(() => {
    const urls = blobUrlByRecordRef.current;
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
      urls.clear();
    };
  }, []);

  const saveSurveyResponse = useCallback(
    async (qid: number, responseText: string): Promise<void> => {
      const taskUUID = currentTaskUUIDRef.current;
      if (!taskUUID) {
        console.warn('[useVisitTask] saveSurveyResponse called without active task');
        return;
      }
      setSurveyResponses((prev) => {
        const idx = prev.findIndex((r) => r.qid === qid);
        const next: SurveyResponseEntry[] =
          idx >= 0
            ? prev.map((r) => (r.qid === qid ? { qid, responseText } : r))
            : [...prev, { qid, responseText }];
        // Persist off-render
        void putSurveyResponse({
          taskUUID,
          responses: next,
          updatedAt: new Date().toISOString(),
          syncedAt: null,
        });
        return next;
      });
    },
    []
  );

  const addFile = useCallback(async (file: File, type: 'photo' | 'document'): Promise<void> => {
    const taskUUID = currentTaskUUIDRef.current;
    if (!taskUUID) {
      console.warn('[useVisitTask] add', type, 'called without active task');
      return;
    }
    const capturedAt = new Date().toISOString();
    await putFileQueueEntry({
      taskUUID,
      type,
      blob: file,
      filename: file.name,
      mimeType: file.type,
      capturedAt,
      syncStatus: 'pending',
      syncAttempts: 0,
    });
    // Reload this task's files to pick up the autoincrement id. Existing files
    // REUSE their stable URL (the per-record cache) and only the newly-added
    // file mints one — no revoke churn. This is what originally fixed the
    // leak in #2127 (every add used to mint fresh URLs for EVERY file), now
    // without the revoke-the-displayed-URL flash that broke thumbnails (#55).
    const files = await getFileQueueForTask(taskUUID);
    const photos: UploadedPhotoLike[] = [];
    const docs: UploadedDocumentLike[] = [];
    for (const rec of files) {
      if (rec.type === 'photo') {
        photos.push(hydratePhotoRecord(rec, stableUrlForRecord(rec)));
      } else {
        docs.push(hydrateDocumentRecord(rec, stableUrlForRecord(rec)));
      }
    }
    setPhotoUploads(photos);
    setDocumentUploads(docs);
  }, []);

  const addPhoto = useCallback((file: File) => addFile(file, 'photo'), [addFile]);
  const addDocument = useCallback((file: File) => addFile(file, 'document'), [addFile]);

  const removeFileById = useCallback(async (uploadId: string): Promise<void> => {
    // uploadId format: `${type}-${recordId}`
    const recordId = Number(uploadId.split('-').pop());
    if (!Number.isFinite(recordId)) return;
    await deleteFileQueueEntry(recordId);
    // The record is gone for good — revoke its one stable URL.
    const url = blobUrlByRecordRef.current.get(recordId);
    if (url) {
      URL.revokeObjectURL(url);
      blobUrlByRecordRef.current.delete(recordId);
    }
    setPhotoUploads((prev) => prev.filter((u) => u.__recordId !== recordId));
    setDocumentUploads((prev) => prev.filter((u) => u.__recordId !== recordId));
  }, []);

  // Durable thumbnail recovery (#2127, diag #54). When iOS WKWebView evicts a
  // blob's backing under memory pressure, BOTH the displayed blob: URL and the
  // in-memory File share the dead backing, so re-minting from the File yields
  // another broken URL. The persisted copy in visit_file_queue (IDB) survives
  // eviction, so re-read it by record id and mint a fresh, tracked URL. Returns
  // null only when the record/blob is truly gone (then the UI may show broken).
  const recoverFileBlobUrl = useCallback(async (uploadId: string): Promise<string | null> => {
    const recordId = Number(uploadId.split('-').pop());
    if (!Number.isFinite(recordId)) return null;
    try {
      const rec = await getFileQueueEntry(recordId);
      if (!rec || !rec.blob) return null;
      const url = URL.createObjectURL(rec.blob);
      // Replace the evicted URL in the per-record cache so future re-renders
      // reuse this fresh one (and free the dead handle).
      const dead = blobUrlByRecordRef.current.get(recordId);
      if (dead && dead !== url) {
        try {
          URL.revokeObjectURL(dead);
        } catch {
          /* already gone */
        }
      }
      blobUrlByRecordRef.current.set(recordId, url);
      return url;
    } catch (err) {
      console.error('[useVisitTask] recoverFileBlobUrl failed:', err);
      return null;
    }
  }, []);

  const removePhoto = useCallback((id: string) => removeFileById(id), [removeFileById]);
  const removeDocument = useCallback((id: string) => removeFileById(id), [removeFileById]);

  return {
    visitState,
    isLoading,
    isHydrated,
    error,
    startVisit,
    setDriveData,
    setGPSData,
    startTask,
    reconcileCurrentTaskWorkflow,
    pauseCurrentTask,
    resumePausedTask,
    discardPausedTask,
    finishCurrentTask,
    abandonCurrentTask,
    getCompletedTaskIds,
    getPausedTaskId,
    getTotalTimeMinutes,
    getCurrentTaskLocalId,
    surveyResponses,
    saveSurveyResponse,
    photoUploads,
    documentUploads,
    addPhoto,
    addDocument,
    removePhoto,
    removeDocument,
    recoverFileBlobUrl,
    submitAllTasks,
    resetVisit,
  };
}

// ---------- Helpers ----------

function hydrateCommon(rec: FileQueueRecord, dataUrl: string) {
  // Construct a File from the stored Blob so submission code that calls
  // .name / new FormData() etc keeps working without changes. The dataUrl is
  // the record's STABLE blob: URL (minted once, reused across hydrations) —
  // passed in so the hook owns URL lifetime (#2127 / diag #55).
  const file =
    rec.blob instanceof File
      ? rec.blob
      : new File([rec.blob], rec.filename, { type: rec.mimeType });
  return {
    id: `${rec.type}-${rec.id}`,
    name: rec.filename,
    dataUrl,
    file,
    timestamp: new Date(rec.capturedAt),
    __recordId: rec.id,
  };
}

function hydratePhotoRecord(rec: FileQueueRecord, dataUrl: string): UploadedPhotoLike {
  return { ...hydrateCommon(rec, dataUrl), type: 'photo' };
}

function hydrateDocumentRecord(rec: FileQueueRecord, dataUrl: string): UploadedDocumentLike {
  return { ...hydrateCommon(rec, dataUrl), type: 'document' };
}
